import { getDb, type SavedSearchRow } from './schema';

/**
 * Saved searches store. Wraps the IndexedDB `savedSearches` object store
 * with a small, source-agnostic CRUD API. The UI surfaces saved entries
 * via a dropdown on the search bar; nothing in here knows about routes
 * or React.
 *
 * Source convention:
 *   - `source === sourceId` for source-scoped entries (e.g. `'hitomi'`).
 *   - `source === '*'` for cross-source aggregate entries (the `/search`
 *     page). We could use `null` instead but a sentinel string keeps the
 *     index simpler and matches the blacklist's `'*'` convention.
 */

export type SavedSearch = SavedSearchRow;

export interface CreateSavedSearchInput {
  name: string;
  source: string;
  url: string;
}

/** Crude but sufficient: random alphanum + timestamp suffix. */
function makeId(): string {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${rand}`;
}

export async function listSavedSearches(): Promise<SavedSearch[]> {
  const db = await getDb();
  const rows = await db.getAll('savedSearches');
  return rows.sort((a, b) => b.createdAt - a.createdAt);
}

export async function listSavedSearchesForSource(source: string): Promise<SavedSearch[]> {
  const db = await getDb();
  const rows = await db.getAllFromIndex('savedSearches', 'by-source', source);
  return rows.sort((a, b) => b.createdAt - a.createdAt);
}

export async function createSavedSearch(input: CreateSavedSearchInput): Promise<SavedSearch> {
  const db = await getDb();
  const row: SavedSearch = {
    id: makeId(),
    name: input.name.trim(),
    source: input.source,
    url: input.url,
    createdAt: Date.now(),
  };
  if (!row.name) throw new Error('saved search name is required');
  await db.put('savedSearches', row);
  return row;
}

export async function deleteSavedSearch(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('savedSearches', id);
}

export async function getSavedSearch(id: string): Promise<SavedSearch | undefined> {
  const db = await getDb();
  return db.get('savedSearches', id);
}
